import { coerceCssPixelValue } from '@angular/cdk/coercion';

import { SnLonLatBounds, SnBounds, SnLonLatBoundsLike, SnLonLat } from '@lib/geo/helpers';
import { THEME } from '@lib/ui/theme';

import { MapBaseOverlay } from './base-overlay';
import { IMapBaseOverlayOptions } from './base-overlay-options';

export class GoogleMapImageOverlay extends MapBaseOverlay {
  private readonly _url: string;
  private readonly _rotation: number;
  private readonly _lonLatBounds: SnLonLatBounds;
  private readonly _latLng: google.maps.LatLng;
  private readonly _imageSize: google.maps.Size;
  private readonly _markerSize: google.maps.Size;
  private readonly _imageLabel: string;

  private readonly _element: HTMLElement;
  private readonly _imageElement: HTMLImageElement;
  private readonly _textBoxElement: HTMLDivElement;

  constructor(
    url: string,
    rotation: number = 0,
    lonLatBounds?: SnLonLatBoundsLike,
    options?: IMapBaseOverlayOptions,
    latLng?: google.maps.LatLng,
    markerSize?: google.maps.Size,
    imageLabel?: string,
  ) {
    super(options);

    this._url = url;
    this._rotation = rotation;
    this._lonLatBounds = SnLonLatBounds.convert(lonLatBounds);
    this._latLng = latLng;
    this._imageSize = new google.maps.Size(80, 80);
    this._markerSize = markerSize;
    this._imageLabel = imageLabel;

    this._element = this._createElement();
    this._imageElement = this._createImageElement();
    if (this._imageLabel) {
      this._textBoxElement = this._createTextBoxElement();
      this._element.appendChild(this._textBoxElement);
    }

    this._initInnerWatchers();
  }

  get latLng(): google.maps.LatLng {
    return this._latLng;
  }

  public roomsCleared(): void {
    this._imageElement.style.filter = `drop-shadow(0px 0px 20px ${THEME.success})`;
  }

  public removeRoomsCleared(): void {
    this._imageElement.style.filter = '';
  }

  protected draw(): void {
    this._updatePosition();
    this._updateSize();
  }

  protected onAdd(): void {
    this._updateZIndex();
    this._updateOpacity();

    this.getPanes().overlayLayer.appendChild(this._element);
  }

  protected onRemove(): void {
    this._element.parentElement.removeChild(this._element);
  }

  private _updateZIndex(): void {
    const zIndex = this.getZIndex();
    this._element.style.zIndex = zIndex.toString();
  }

  private _updateOpacity(): void {
    const opacity = this.getOpacity();
    this._element.style.opacity = opacity.toString();
  }

  private _updatePosition(): void {
    const element = this._element;
    const topLeft = this._latLng
      ? this.fromLonLatToPoint(new SnLonLat(this._latLng.lng(), this._latLng.lat()))
      : this.fromLonLatToPoint(this._lonLatBounds.getNorthWest());
    element.style.left = coerceCssPixelValue(topLeft.x);
    element.style.top = coerceCssPixelValue(topLeft.y);
    if (this._latLng) {
      element.style.width = coerceCssPixelValue(this._imageSize.width);
      element.style.height = coerceCssPixelValue(this._imageSize.height);
      element.style.marginLeft = coerceCssPixelValue(-(this._imageSize.width / 2));
      element.style.marginTop = coerceCssPixelValue(
        -(this._imageSize.height + 20 + this._markerSize.height),
      );
      element.style.borderRadius = this._imageLabel ? '50% 0 0 50%' : '50%';
      element.classList.add('fs__pano-image-overlay');
    }
  }

  private _updateSize(): void {
    const image = this._imageElement;
    if (this._lonLatBounds) {
      const topLeft = this.fromLonLatToPoint(this._lonLatBounds.getNorthWest());
      const bottomRight = this.fromLonLatToPoint(this._lonLatBounds.getSouthEast());
      const bounds = new SnBounds(topLeft, bottomRight);

      const size = bounds.getSize();
      image.style.width = coerceCssPixelValue(size.x);
      image.style.height = coerceCssPixelValue(size.y);
    } else {
      image.style.width = coerceCssPixelValue(this._imageSize.width - 10);
      image.style.height = coerceCssPixelValue(this._imageSize.height - 10);
    }
  }

  private _initInnerWatchers(): void {
    this.on('zIndexChange').subscribe(this._updateZIndex.bind(this));
    this.on('opacityChange').subscribe(this._updateOpacity.bind(this));
  }

  private _createElement(): HTMLElement {
    const element = document.createElement('div');
    element.style.position = 'absolute';
    element.style.transform = `rotate(${this._rotation}deg)`;

    return element;
  }

  private _createImageElement(): HTMLImageElement {
    const image = document.createElement('img');
    image.src = this._url;
    image.crossOrigin = 'Anonymous';
    this._element.appendChild(image);

    return image;
  }

  private _createTextBoxElement(): HTMLDivElement {
    const element = document.createElement('div');
    const text = document.createElement('div');
    text.innerHTML = this._imageLabel;
    element.appendChild(text);
    element.style.position = 'absolute';
    element.style.width = coerceCssPixelValue(this._imageSize.width - 10);
    element.style.right = coerceCssPixelValue(-(this._imageSize.width - 10));
    element.style.height = '100%';
    element.classList.add('fs__image-overlay-textbox');
    text.classList.add('fs-text-truncate');

    return element;
  }
}
